/**
 * Theme management — Phase 5 (full light + dark support).
 *
 * Architecture:
 *  - CSS variables в globals.css описывают оба набора tokens (light + dark).
 *  - `<html data-theme="light|dark">` атрибут переключает explicit override.
 *  - Перед первым решением JS — initial paint matches system preference
 *    через `@media (prefers-color-scheme: dark)` в globals.css → нет flash
 *    of wrong theme.
 *
 * Detection priority:
 *  1. MAX SDK `bridgeInstance.colorScheme` — если 'light' | 'dark'.
 *  2. window.matchMedia('(prefers-color-scheme: dark)') — fallback.
 *  3. 'light' — SSR / тестовое окружение без window.
 *
 * Theme tokens / цветовая палитра — docs/design-system.md +
 * globals.css :root + @media + [data-theme] блоки. Dark theme дизайнерское
 * решение (Phase 5): deep slate background, soft off-white text, slightly
 * brightened brand-orange accent для контраста на тёмном.
 */

import { getMaxBridge } from './max-bridge';

export type Theme = 'light' | 'dark';

/** Detect system preferred theme via matchMedia. SSR-safe. */
function detectFromMatchMedia(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Initial theme — читаем сначала MAX SDK, потом matchMedia.
 *
 * MAX SDK `colorScheme` populated на Android/iOS WebView правильно
 * отражает текущую тему MAX-приложения (на Android при системной тёмной
 * теме MAX тоже dark — и Mini App теперь honor'ит это решение, а не
 * борется с force-dark).
 */
export function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const bridge = getMaxBridge();
    if (bridge.colorScheme === 'dark' || bridge.colorScheme === 'light') {
      return bridge.colorScheme;
    }
  } catch {
    // Bridge может быть ещё не инициализирован — fallback ниже.
  }
  return detectFromMatchMedia();
}

/**
 * Apply theme к DOM — выставляет `<html data-theme="...">`.
 * CSS selectors `:root[data-theme='light'|'dark']` подхватывают и
 * переключают tokens. Idempotent — повторный вызов с тем же значением
 * не вызывает reflow (тот же атрибут).
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (html.getAttribute('data-theme') !== theme) {
    html.setAttribute('data-theme', theme);
  }
  // Legacy: убрать .dark класс, если был от прежних версий.
  html.classList.remove('dark');
}

/**
 * Subscribe to system theme changes. Returns unsubscribe-функцию.
 * SSR-safe — возвращает no-op если нет window.matchMedia.
 *
 * MAX SDK теоретически может emit'ить themeChanged event. Если bridge
 * exposes onEvent, ThemeContext подпишется отдельно — здесь только
 * matchMedia fallback.
 */
export function subscribeSystemTheme(handler: (theme: Theme) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => undefined;
  }
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent | MediaQueryList) => {
    handler(e.matches ? 'dark' : 'light');
  };
  // addEventListener vs addListener — обе нужны на разных версиях WebView.
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }
  // Старые Android WebView: deprecated addListener.
  (mq as { addListener?: (l: (e: MediaQueryListEvent) => void) => void }).addListener?.(
    listener as (e: MediaQueryListEvent) => void,
  );
  return () => {
    (mq as { removeListener?: (l: (e: MediaQueryListEvent) => void) => void }).removeListener?.(
      listener as (e: MediaQueryListEvent) => void,
    );
  };
}
